import { ONBOARDING_ROUTES } from "../onboarding/onboardingRoutes";
import { AUTH_ROUTES } from "./authRoutes";
import React, { useEffect, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import "./SignIn.css";
import VerificationBg from "../assets/images/verification-bg.svg";
import VerifyImg from "../assets/images/verify-img.svg";
import { BackSVG } from "../components/SvgIcons";
import { resendSignUpOtp, verifySignUpOtp } from "../services/authService";
import { useAuth as useAuthContext } from "../context/AuthContext";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 30;

function SignUpVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthContext();
  const [otp, setOtp] = useState(Array.from({ length: OTP_LENGTH }, () => ""));
  const [timer, setTimer] = useState(RESEND_SECONDS);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef([]);

  const userId = location.state?.userId;
  const email = location.state?.email || "";

  useEffect(() => {
    if (!userId) {
      navigate(AUTH_ROUTES.signUp, { replace: true });
    }
  }, [navigate, userId]);

  useEffect(() => {
    if (timer <= 0) {
      return undefined;
    }

    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (event, index) => {
    const value = event.target.value;
    if (!/^[0-9]?$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setSubmitError("");
    setSubmitSuccess("");

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleResend = async () => {
    if (!userId || timer > 0 || isResending) {
      return;
    }

    setIsResending(true);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const { meta } = await resendSignUpOtp({ user_id: userId });
      setTimer(RESEND_SECONDS);
      setOtp(Array.from({ length: OTP_LENGTH }, () => ""));
      setSubmitSuccess(meta?.message || "Verification code resent successfully.");
      inputRefs.current[0]?.focus();
    } catch (error) {
      setSubmitError(error.message || "Unable to resend the verification code.");
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = async () => {
    if (!userId) {
      return;
    }

    const verificationCode = otp.join("");
    if (verificationCode.length !== OTP_LENGTH) {
      setSubmitError("Enter the 4-digit verification code.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const { payload, meta } = await verifySignUpOtp({
        user_id: userId,
        verification_code: verificationCode,
      });

      if (!payload?.access_token) {
        throw new Error(meta?.message || "Verification failed. Please try again.");
      }

      login({
        accessToken: payload.access_token,
        user: payload.user,
        expiresIn: payload.expires_in,
        tokenType: payload.token_type,
      });

      navigate(ONBOARDING_ROUTES.states, { replace: true });
    } catch (error) {
      setSubmitError(error.message || "Unable to verify the code right now.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid signin-container auth-background">
      <div className="row auth-page-row">
        <div className="col-md-6 d-flex flex-column justify-content-center align-items-center text-white left-section p-0 verifiy-LeftSection">
          <div className="verificationBg">
            <img src={VerificationBg} alt="" className="img-fluid mb-4" />
          </div>
        </div>

        <div className="col-md-6 d-flex justify-content-center align-items-center right-section position-relative signUp-Box verification-box mobile-display">
          <div className="backIcon ps-4 pe-4 d-block d-md-none" onClick={() => navigate(AUTH_ROUTES.signUp)}>
            <BackSVG />
          </div>
          <div className="signin-mobile ps-4 pe-4 verify-mobile d-block d-md-none mx-auto text-center">
            <img src={VerifyImg} alt="" className="mx-auto" />
          </div>

          <div className="signin-box auth-form-card p-4 p-md-5 rounded text-center">
            <h3 className="fw-semibold text-center mb-2">Sign Up Verification</h3>
            <p className="text-center fw-medium form-paagraph mobile-paragraph auth-form-intro">
              Enter the four digit code sent to your Email{email ? ` (${email})` : ""}.
            </p>

            {submitError ? (
              <Alert className="mb-3 text-start" variant="danger">
                {submitError}
              </Alert>
            ) : null}

            {submitSuccess ? (
              <Alert className="mb-3 text-start" variant="success">
                {submitSuccess}
              </Alert>
            ) : null}

            <div className="d-flex justify-content-center gap-3 mb-3 verify-code auth-otp-field">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  placeholder="0"
                  value={digit}
                  onChange={(event) => handleChange(event, index)}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                  ref={(element) => {
                    inputRefs.current[index] = element;
                  }}
                  className="otp-input text-center"
                />
              ))}
            </div>

            <p className="mb-0 auth-resend">
              <span
                className={`resend-text text-purple fw-bold ${timer === 0 ? "active" : ""}`}
                onClick={timer === 0 ? handleResend : undefined}
                role="button"
                tabIndex={timer === 0 ? 0 : -1}
              >
                {isResending ? "Resending..." : "Resend code"}
              </span>
              : <span className="fw-bold resend-timer">00:{timer.toString().padStart(2, "0")}</span>
            </p>

            <button
              className="signin-btn w-100 fw-semibold px-5 py-2 btn btn-primary mt-4"
              type="button"
              onClick={handleVerify}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Verifying..." : "Verify"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignUpVerification;
